import { ApexOptions } from 'apexcharts';

import { CHART_COLORS } from '../shared/chart-colors';
import { buildChartPointTooltipHtml, buildChartSharedPointTooltipHtml } from '../shared/chart-tooltip';
import { formatAxisDayNumber, formatAxisLabel, formatAxisMonthLabel } from '../shared/format-axis-label';

export type LineChartLabelMode = boolean | 'minimal';
export type LineChartLabelFormat = 'default' | 'month';

export interface LineChartOptionsConfig {
    categories: string[];
    height: number;
    width?: number;
    colors?: string[];
    showArea?: boolean;
    showLegend?: boolean;
    showLabels?: LineChartLabelMode;
    labelFormat?: LineChartLabelFormat;
    tooltipShared?: boolean;
    tooltipSeriesLabel?: string;
    /** Horizontal Y annotation (e.g. SLOT capacity). */
    reference?: number | null;
    referenceLabel?: string;
    /** Highest series value — used with `reference` to keep the annotation in range. */
    seriesMax?: number;
}

const MINIMAL_X_LABELS = 5;

const shouldShowMinimalCategoryLabel = (index: number, count: number): boolean => {
    if (count <= MINIMAL_X_LABELS) {
        return true;
    }

    const step = Math.max(1, Math.ceil((count - 1) / (MINIMAL_X_LABELS - 1)));
    return index === count - 1 || index % step === 0;
};

const hasFiniteReference = (reference: number | null | undefined): reference is number => typeof reference === 'number' && Number.isFinite(reference);

/** Annotation text for a horizontal reference line (e.g. SLOT capacity · 48). */
export const formatReferenceAnnotationLabel = (reference: number, referenceLabel?: string): string => {
    const label = referenceLabel?.trim();
    return label ? `${label} · ${reference}` : String(reference);
};

export const getLineChartOptions = (config: LineChartOptionsConfig): ApexOptions => {
    const {
        categories,
        height,
        width,
        colors = [...CHART_COLORS],
        showArea = true,
        showLegend = false,
        showLabels = true,
        labelFormat = 'default',
        tooltipShared = false,
        tooltipSeriesLabel,
        reference,
        referenceLabel,
        seriesMax = 0
    } = config;
    const labelsVisible = showLabels !== false;
    const sparseLabels = showLabels === 'minimal';
    const showYearInMonthLabel = new Set(categories.map((category) => category.slice(0, 4))).size > 1;
    const formatLabel =
        labelFormat === 'month' ? (label: unknown) => formatAxisMonthLabel(label, showYearInMonthLabel) : (label: unknown) => formatAxisLabel(label);
    const showReference = hasFiniteReference(reference);
    const yAxisMax = showReference ? Math.max(seriesMax, reference, 0) : undefined;

    const options: ApexOptions = {
        chart: {
            height,
            width: width || '100%',
            type: showArea ? 'area' : 'line',
            zoom: {
                enabled: false,
                allowMouseWheelZoom: false
            },
            parentHeightOffset: 0,
            toolbar: {
                show: false
            }
        },
        dataLabels: {
            enabled: false
        },
        grid: {
            show: false,
            padding: {
                left: labelsVisible ? 4 : 0,
                right: labelsVisible ? 4 : 0
            }
        },
        xaxis: {
            categories,
            axisTicks: {
                show: false
            },
            axisBorder: {
                show: false
            },
            labels: {
                show: labelsVisible,
                rotate: 0,
                hideOverlappingLabels: true,
                style: {
                    cssClass: sparseLabels ? 'apexcharts-xaxis-label fill-brand-muted' : 'apexcharts-xaxis-label fill-foreground-default'
                },
                formatter: (value, _timestamp, opts) => {
                    const index = opts?.i;
                    if (sparseLabels && typeof index === 'number' && !shouldShowMinimalCategoryLabel(index, categories.length)) {
                        return '';
                    }

                    const label = typeof index === 'number' ? (categories[index] ?? String(value)) : String(value);
                    return sparseLabels && labelFormat !== 'month' ? formatAxisDayNumber(label) : formatLabel(label);
                }
            },
            tooltip: {
                enabled: false
            }
        },
        yaxis: {
            min: 0,
            ...(yAxisMax != null ? { max: yAxisMax } : {}),
            tickAmount: sparseLabels ? 2 : undefined,
            decimalsInFloat: sparseLabels ? 0 : undefined,
            show: labelsVisible,
            labels: {
                show: labelsVisible,
                formatter: sparseLabels ? (value) => String(Math.round(Number(value))) : undefined,
                style: {
                    colors: [sparseLabels ? 'var(--color-brand-muted)' : 'var(--color-foreground-default)']
                }
            }
        },
        colors,
        stroke: {
            curve: 'smooth',
            width: 2
        },
        // ApexCharts reads fill.colors during init — never leave fill undefined.
        fill: showArea
            ? {
                  type: 'gradient',
                  gradient: {
                      shade: 'light',
                      type: 'vertical',
                      shadeIntensity: 1,
                      gradientToColors: colors,
                      inverseColors: false,
                      opacityFrom: 0.3,
                      opacityTo: 0.05,
                      stops: [0, 90, 100]
                  }
              }
            : {
                  type: 'solid',
                  opacity: 0
              },
        markers: {
            size: 0,
            hover: {
                size: 5
            }
        },
        legend: {
            show: showLegend,
            position: 'top',
            horizontalAlign: 'left',
            labels: {
                colors: 'var(--color-foreground-default)'
            },
            onItemHover: {
                highlightDataSeries: true
            }
        },
        tooltip: {
            enabled: true,
            shared: tooltipShared,
            intersect: !tooltipShared,
            custom: (context) => {
                const label = categories[context.dataPointIndex] ?? context.w.globals.labels[context.dataPointIndex] ?? '';

                if (tooltipShared) {
                    const configuredSeries = (context.w.config.series ?? []) as { name?: string }[];
                    const globalColors = (context.w.globals.colors ?? []) as string[];
                    const configuredColors = (context.w.config.colors ?? []) as string[];
                    const series = configuredSeries.map((item, index) => ({
                        name: item?.name,
                        value: context.w.globals.series?.[index]?.[context.dataPointIndex],
                        color:
                            globalColors[index] ?? configuredColors[index] ?? configuredColors[index % configuredColors.length] ?? colors[index % colors.length]
                    }));

                    return buildChartSharedPointTooltipHtml(label, series, tooltipSeriesLabel, formatLabel);
                }

                const seriesValues = context.w.globals.series?.[context.seriesIndex];
                const value = seriesValues?.[context.dataPointIndex];
                const seriesName = context.w.config.series?.[context.seriesIndex]?.name || undefined;

                return buildChartPointTooltipHtml(label, value, seriesName, tooltipSeriesLabel, formatLabel);
            }
        }
    };

    if (!showReference) {
        return options;
    }

    return {
        ...options,
        grid: {
            ...options.grid,
            padding: {
                left: labelsVisible ? 4 : 0,
                right: labelsVisible ? 8 : 4,
                top: 12
            }
        },
        // ApexCharts always reads annotations.images / points / etc. — never pass `annotations: undefined`.
        annotations: {
            yaxis: [
                {
                    y: reference,
                    borderColor: 'var(--color-brand-muted)',
                    strokeDashArray: 6,
                    label: {
                        text: formatReferenceAnnotationLabel(reference, referenceLabel),
                        borderColor: 'transparent',
                        position: 'right',
                        offsetX: -4,
                        offsetY: -4,
                        textAnchor: 'end',
                        style: {
                            color: 'var(--color-brand-muted)',
                            background: 'var(--color-background-default)',
                            fontSize: '11px',
                            fontWeight: 500
                        }
                    }
                }
            ]
        }
    };
};
